/**
 * Horloge modulaire — arithmétique pure modulo n.
 */

/** Reste euclidien, toujours entre 0 et n − 1 (aussi pour a négatif). */
export function mod(a: number, n: number): number {
  return ((a % n) + n) % n;
}

/** a = q × n + r avec 0 ≤ r < n */
export function divmod(a: number, n: number): { q: number; r: number } {
  const r = mod(a, n);
  return { q: (a - r) / n, r };
}

export function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a;
}

export function isPrime(n: number): boolean {
  if (n < 2) return false;
  for (let d = 2; d * d <= n; d++) if (n % d === 0) return false;
  return true;
}

/** 0, a, 2a, … modulo n jusqu'au retour en 0 (exclu) : n / pgcd(a, n) points. */
export function multiplesOrbit(a: number, n: number): number[] {
  const out: number[] = [0];
  let x = mod(a, n);
  while (x !== 0 && out.length <= n) {
    out.push(x);
    x = mod(x + a, n);
  }
  return out;
}

/** b tel que a × b ≡ 1 [n], ou null si a n'est pas inversible. */
export function inverse(a: number, n: number): number | null {
  if (n === 1) return null;
  for (let b = 1; b < n; b++) if (mod(a * b, n) === 1) return b;
  return null;
}

/**
 * Puissances a⁰, a¹, a², … modulo n jusqu'à la première répétition :
 * `values[k]` = aᵏ mod n ; le cycle commence à `start` et dure `period`.
 */
export function powerCycle(
  a: number,
  n: number,
): { values: number[]; start: number; period: number } {
  const values: number[] = [];
  const seen = new Map<number, number>();
  let x = mod(1, n);
  for (let k = 0; k <= n + 1; k++) {
    const at = seen.get(x);
    if (at !== undefined) return { values, start: at, period: k - at };
    seen.set(x, k);
    values.push(x);
    x = mod(x * a, n);
  }
  return { values, start: 0, period: values.length };
}

/** aᵏ mod n par exponentiation rapide (petits nombres) */
export function powMod(a: number, k: number, n: number): number {
  let result = mod(1, n);
  let base = mod(a, n);
  let e = k;
  while (e > 0) {
    if (e & 1) result = mod(result * base, n);
    base = mod(base * base, n);
    e >>= 1;
  }
  return result;
}

export const N_MIN = 2;
export const N_MAX = 24;
